import dayjs from "dayjs";
import "dayjs/locale/es-mx";
import { toCents } from "./money";
import type { Frequency, Money } from "./types";

dayjs.locale("es-mx");

const mxn = new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" });
const mxnCompact = new Intl.NumberFormat("es-MX", {
  style: "currency",
  currency: "MXN",
  maximumFractionDigits: 0,
});
const compactShort = new Intl.NumberFormat("es-MX", {
  notation: "compact",
  maximumFractionDigits: 1,
});

/** `compact` drops cents from $10,000 up; `whole` always drops them (dense tables). */
export function formatMoney(
  value: Money | number,
  opts: { compact?: boolean; whole?: boolean } = {},
): string {
  const n = toCents(value) / 100;
  return (opts.whole || (opts.compact && Math.abs(n) >= 10000) ? mxnCompact : mxn).format(n);
}

export const formatAxis = (n: number): string => `$${compactShort.format(n)}`;

export const formatDate = (iso: string, fmt = "D MMM"): string =>
  dayjs(iso).format(fmt).replace(".", "");

export const formatWeekday = (iso: string): string => formatDate(iso, "ddd D MMM");

export function relativeDay(iso: string, today = dayjs()): string {
  const diff = dayjs(iso).startOf("day").diff(today.startOf("day"), "day");
  if (diff === 0) return "Hoy";
  if (diff === 1) return "Mañana";
  if (diff === -1) return "Ayer";
  if (diff > 1 && diff < 7) return `En ${diff} días`;
  if (diff < -1 && diff > -7) return `Hace ${-diff} días`;
  return formatDate(iso);
}

export const FREQUENCY_LABEL: Record<Frequency, string> = {
  monthly: "mensual",
  biweekly: "quincenal",
  fortnightly: "catorcenal",
  quarterly: "trimestral",
  semiannual: "semestral",
  yearly: "anual",
  once: "una vez",
};

export const todayISO = (): string => dayjs().format("YYYY-MM-DD");

/** "48 s", "1 min 35 s": how long reading one statement took. */
export function formatSeconds(value: string): string {
  const total = Math.round(Number(value));
  if (total < 60) return `${total} s`;
  const rest = total % 60;
  return `${Math.floor(total / 60)} min${rest ? ` ${rest} s` : ""}`;
}
